import {Component, Input, OnInit} from '@angular/core';
import {
  Feature,
  LinkedDepartments,
  LinkedDepartmentsLinkType,
  PriceBand,
  StoreInfo,
  SubDep,
} from '../../../../shared-utilities/models-old/datastructures';
import {SelectPopoverComponent} from '../select-popover/select-popover.component';
import {copyRange, inRange, IRange, rangeOverlap} from '../../../../shared-utilities/functions-old/ranges';
import {
  DepInfo,
  SuggestedLink,
  UnlinkedDepartmentsModalComponent,
} from '../unlinked-departments-modal/unlinked-departments-modal.component';
import {initObj} from '../../../../shared-utilities/functions-old/object-functions';
import {FirebaseService} from '../../../../shared-utilities/services-old/firebase.service';

import {IconsEnum} from '../../../shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../shared-icons/models/icons-colour.enum';
import {ModalControllerService} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {PopoverControllerService} from '../../../../shared/shared-services/overlay/popover-controller.service';
import {IDepartment} from '../../../../shared/shared-models/stock/departments';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';

type SnapType = 'rand' | 'cents';

@Component({
  selector: 'app-price-banding',
  templateUrl: './price-banding.component.html',
  styleUrls: ['./price-banding.component.scss'],
  standalone: false,
})
export class PriceBandingComponent implements OnInit {

  @Input() stores?: {order: string[]; stores: Record<string, StoreInfo>};
  @Input() storeId?: string;
  @Input() setChanges?: <TChange extends Record<number, PriceBand>>(
    document: string,
    change: TChange,
    storeId?: string,
    feature?: Feature | 'user'
  ) => void;

  @Input() departments?: Record<string,
    {
      dep: Record<string, {name: string}>;
      sub: Record<string, SubDep>;
    }>;

  priceBands: Record<string, PriceBand[]> = {};
  ogPriceBands: Record<string, PriceBand[]> = {};
  selectedStore = null;
  hiddenPBs: Record<string, Record<string, boolean>[]> = {};
  toggles: Record<string, {range?: boolean; randSnap?: boolean}[]> = {};
  randSnapConstraints: Record<string, {digs: number; selectable: number}[]> = {};
  copiedStoreId = '';
  copiedIdx: number[] = [];
  copiedPriceBand: PriceBand[] = [];

  readonly randSnapsIDs = ['a', 'b', 'c', 'd', 'e'];

  readonly help = {
    copySingleRule: 'Copy Rule', copyAll: 'Copy All Rules',
    paste: 'Paste Copied Rules', clearClipboard: 'Clear Copied Rules',
  };
  readonly centSnapsIDs = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];
  // eslint-disable-next-line @typescript-eslint/member-ordering
  protected readonly icons = IconsEnum;
  protected readonly iconColourEnum = IconColourEnum;
  private depLinks: LinkedDepartments = {idxLookUp: {}, sIdLookUp: {}};
  // TODO: This should almost definitely be local only. Want to decide each time.
  private makeNewLinks: Record<string, boolean> = {};
  private alreadyAlerted: boolean;

  constructor(
    private firebase: FirebaseService,
    private modalController: ModalControllerService,
    private popControl: PopoverControllerService,
    private readonly sharedModalUtils: SharedModalUtils,
  ) {
  }

  private static copyPB(pb: PriceBand): PriceBand {
    const pb2: PriceBand = {};

    if (pb.range) {
      pb2.range = copyRange(pb.range);
    }
    if (pb.departs) {
      pb2.departs = pb.departs.map((d) => d);
    }
    if (pb.subDeparts) {
      pb2.subDeparts = pb.subDeparts.map((d) => d);
    }
    if (pb.randSnap) {
      pb2.randSnap = {
        digits: pb.randSnap.digits,
        snaps: pb.randSnap.snaps.map((cs) => ({range: copyRange(cs.range), v: cs.v})),
      };
    }
    if (pb.centSnaps) {
      pb2.centSnaps = pb.centSnaps.map((cs) => ({range: copyRange(cs.range), v: cs.v}));
    }
    return pb2;
  }

  private static invalidSnap(snap: {range: IRange; v: number}, snapType: SnapType, pbRange?: IRange): string {
    if (snap.range) {
      for (const part of Object.keys(snap.range)) {
        if (snap.range[part]) {
          if (snap.range[part].v !== null && isNaN(+snap.range[part].v)) {
            return `Range ${part} is not valid`;
          }

          if (snapType === 'cents') {
            if (snap.range[part].v < 0 || snap.range[part].v > 99) {
              return `Range ${part} is not between 0 and 99`;
            }
          } else if (pbRange) {
            if (!inRange(snap.range[part].v, pbRange)) {
              return `Range ${part} is not within Price Band range`;
            }
          }
        }
      }

      if (snap.range.start && snap.range.start.v !== null && snap.range.end && snap.range.end.v !== null &&
        snap.range.start.v >= snap.range.end.v) {
        return `${snap.range.start.v} - ${snap.range.end.v} is an invalid range`;
      }
    }

    if (snapType === 'cents') {
      if (snap.v === null || snap.v === undefined || snap.v < 0 || snap.v > 99) {
        return 'The set to value must be between 0 and 99 inclusive';
      }
    } else {
      if (snap.v === null || snap.v === undefined || (pbRange && !inRange(snap.v, pbRange))) {
        return 'The set to value must be within Price Band range';
      }
    }
  }

  private static nines(num: number): number {
    let s = '';
    for (let i = 0; i < num; i++) {
      s += '9';
    }
    return +s;
  }

  private static randSnapRange(digits: number): IRange {
    if (digits <= 0) {
      return {start: {v: 0}, end: {v: 0}};
    }
    const range: IRange = {start: {v: 0, inc: true}, end: {v: 10 ** digits}};
    range.start.v = range.start.v === 1 ? 0 : range.start.v;
    return range;
  }

  async ngOnInit(): Promise<void> {
    if (!this.stores && !this.storeId) {
      throw Error('PriceBandingComponent could not initialise, stores or storeId required');
    }
    const stores = this.stores ? this.stores.order : [this.storeId];

    if (!this.departments) {
      this.departments = {};
      stores.forEach((storeId) => {
        this.departments[storeId] = {dep: null, sub: null};
        this.firebase.getStoreDataDoc<IDepartment>('departments', storeId).then((d) => {
          this.departments[storeId].dep = d;
        }).catch(error => {
          console.error('Error getting departments.\n' + error);
        });
        this.firebase.getStoreDataDoc<SubDep>('sub_departments', storeId).then((d) => {
          this.departments[storeId].sub = d;
        }).catch(error => {
          console.error('Error getting sub_departments.\n' + error);
        });
      });
    }

    await this.firebase.getPriceBands().then((result): void => {

      const process = (storeId: string, pbs: PriceBand[]): void => {
        this.ogPriceBands[storeId] = [];
        this.hiddenPBs[storeId] = [];
        this.toggles[storeId] = [];
        this.randSnapConstraints[storeId] = [];

        if (pbs) {
          this.priceBands[storeId] = pbs;
          let idx = 0;
          pbs.forEach((pb) => {
            this.ogPriceBands[storeId].push(PriceBandingComponent.copyPB(pb));
            this.hiddenPBs[storeId].push({});
            this.toggles[storeId].push({range: !!pb.range, randSnap: !!pb.randSnap});
            this.randSnapConstraints[storeId].push(this.rsMaxSelectableDigits(pb, idx, storeId));
            idx++;
          });
        } else {
          this.priceBands[storeId] = [];
        }

      };
      Object.keys(result.stores).forEach((sID) => {
        process(sID, result.stores[sID]);
      });
      this.stores.order = this.stores.order.filter((storeId: string) => {
        if (result.stores.hasOwnProperty(storeId)) {
          return true;
        }
        delete this.stores.stores[storeId];
        return false;
      });
    });
  }

  storeSelectOptions(): Record<string, ISelectOption> {
    if (!this.stores?.order) {
      return {};
    }

    const options: Record<string, ISelectOption> = {};
    this.stores.order.forEach((storeId: string) => {
      options[storeId] = {
        name: this.stores.stores[storeId].name,
        selected: this.selectedStore === storeId,
        disabled: false,
      };
    });
    return options;
  }

  onStoreSelectionChange(selected: Record<string, ISelectOption>): void {
    const storeId: string | undefined = Object.keys(selected).find((key: string) => selected[key].selected);
    if (!storeId) {
      return;
    }

    this.selectedStore = storeId;
    void this.alertAboutStores();
  }

  async alertAboutStores(): Promise<void> {
    if (!this.alreadyAlerted) {
      await this.presentInfoModal(
        'Important Notice!',
        [
          'Welcome to the Price Banding functionality. This feature has been requested for a while but please note the final layout and implementation of this configurator is still in development.',
          'NOTE: Any settings you change for a store will be applied to all users when editing that store.',
          'Thanks, Techodactyl Team',
        ],
        IconsEnum.infoSlabCircleOutline,
        IconColourEnum.yellow,
      );

      this.alreadyAlerted = true;
    }
  }

  getOgValue(
    part: 'start' | 'end' | 'snap',
    idx: number,
    snapIdx?: number,
    snapType: SnapType = 'cents',
    storeId: string = this.selectedStore): number | null {
    if (this.ogPriceBands[storeId] && this.ogPriceBands[storeId] && this.ogPriceBands[storeId][idx]) {
      const pb = this.ogPriceBands[storeId][idx];

      if (snapIdx !== undefined) {
        if (snapType === 'cents') {
          if (pb.centSnaps.length > snapIdx && pb.centSnaps[snapIdx]) {
            if (part === 'snap') {
              return pb.centSnaps[snapIdx].v;
            } else {
              return pb.centSnaps[snapIdx].range[part].v;
            }
          }
        } else if (pb.randSnap && pb.randSnap.snaps.length > snapIdx && pb.randSnap.snaps[snapIdx]) {
          if (part === 'snap') {
            return pb.randSnap.snaps[snapIdx].v;
          } else {
            return pb.randSnap.snaps[snapIdx].range[part].v;
          }
        }
      } else if (pb.range && pb.range[part]) {
        return pb.range[part].v;
      }
    }
    return null;
  }

  addRule(storeId: string = this.selectedStore): void {
    if (this.selectedStore !== null) {
      if (!this.priceBands[storeId]) {
        this.priceBands[storeId] = [];
        this.ogPriceBands[storeId] = [];
      }
      this.priceBands[storeId].push({} as PriceBand);
      this.ogPriceBands[storeId].push(null);
      this.hiddenPBs[storeId].push({departmentFilters: true});
      this.toggles[storeId].push({});
    }
  }

  private static getInputElement(event: Event): HTMLInputElement {
    return event.target as HTMLInputElement;
  }

  private static getInputValue(event: Event): string {
    return PriceBandingComponent.getInputElement(event).value ?? '';
  }

  setRange(event: Event, range: IRange, part: 'start' | 'end', snapType: SnapType = null, rsDigits?: number): void {
    const inputValue = PriceBandingComponent.getInputValue(event);
    const v = +inputValue;
    let valid = false;
    const alt = part === 'start' ? 'end' : 'start';

    if (inputValue !== '') {
      if (!isNaN(v)) {
        if (range[alt]) {
          if (range[alt].v !== v) {
            if (part === 'start') {
              if (range[alt].v === null || range[alt].v === undefined || v < range[alt].v) {
                valid = true;
              }
            } else if (v > range[alt].v) {
              valid = true;
            }
          }
        } else {
          valid = true;
        }
      }
    } else {
      valid = true;
    }

    if (valid) {
      if (snapType === 'cents') {
        valid = v >= 0 && v <= 99;
      } else if (snapType === 'rand') {
        valid = /^\d+$/.test(inputValue) && v >= 0 && v <= PriceBandingComponent.nines(rsDigits);
      }
    }
    const inputElement = PriceBandingComponent.getInputElement(event);
    const classNames = inputElement.className.split(' ')
      .filter((cn) => cn !== 'invalid').join(' ');
    if (!range[part]) {
      range[part] = {v: null};
    }

    if (!valid) {
      inputElement.className = classNames + ' invalid';

      if (isNaN(v) && inputValue !== '') {
        range[part].v = Number(inputValue);
      } else {
        range[part].v = v;
      }
    } else {
      inputElement.className = classNames;
      range[part].v = v;
    }
  }

  toggleInclusive(pb: PriceBand, part: 'start' | 'end'): void {
    if (pb.range[part]) {
      pb.range[part].inc = !pb.range[part].inc;
    } else {
      pb.range[part] = {v: null, inc: true};
    }
  }

  async filterDeps(event: Event, pb: PriceBand, which: 'dep' | 'sub'): Promise<void> {
    const value = pb[which === 'dep' ? 'departs' : 'subDeparts'];
    const selection: Record<string, string> = {};
    const departs = this.departments[this.selectedStore][which];
    Object.keys(departs).forEach((id) => selection[id] = departs[id].name);

    const order = Object.keys(selection);
    order.sort((a, b) => selection[a] < selection[b] ? -1 : 1);

    const pc = await this.popControl.create({
      component: SelectPopoverComponent,
      componentProps: {
        title: `Filter ${which === 'dep' ? '' : 'Sub-'}Departments`, selection, order, multiple: true,
        value, selectAll: true,
      },
      event,
    });
    await pc.present();
    const {data} = await pc.onDidDismiss<string[]>();

    // TODO: Bailey delete this
    if (data) {
      if (data.length) {
        pb[which === 'dep' ? 'departs' : 'subDeparts'] = data;
      } else {
        delete pb[which === 'dep' ? 'departs' : 'subDeparts'];
      }
    }
  }

  async toggleRange(pb: PriceBand, idx: number): Promise<void> {
    if (!pb.range) {
      pb.range = {};
    } else {
      const keys = Object.keys(pb.range);
      let confirm = false;

      for (const key of keys) {
        if (pb.range[key] && pb.range[key].v !== undefined && pb.range[key].v !== null) {
          confirm = true;
          break;
        }
      }

      if (confirm) {
        const discarded = await this.presentConfirmModal(
          'Discard Range?',
          ['Discard the range values?'],
          'Discard',
          'Cancel',
        );

        if (!discarded) {
          this.syncToggleChecked(idx, 'range', true);
          return;
        }
      }
      delete pb.range;
    }
    this.toggles[this.selectedStore][idx].range = !!pb.range;
  }

  async toggleRandSnaps(pb: PriceBand, idx: number): Promise<void> {
    if (!pb.randSnap) {
      pb.randSnap = {digits: 1, snaps: []};
    } else {
      const confirmed = await this.presentConfirmModal(
        'Remove Rand Snap?',
        ['Are you sure?'],
        'Yes',
        'No',
      );

      if (confirmed) {
        delete pb.randSnap;
      } else {
        this.syncToggleChecked(idx, 'randSnap', true);
        return;
      }
    }
    this.toggles[this.selectedStore][idx].randSnap = !!pb.randSnap;
  }

  /**
   * Forces app-switch-ui back to the intended checked state after a cancelled confirm.
   * Native checkbox state can drift because click updates the input before checkedChange runs.
   * @param idx - Price band index
   * @param key - Toggle key to sync
   * @param checked - Intended checked state
   */
  private syncToggleChecked(idx: number, key: 'range' | 'randSnap', checked: boolean): void {
    this.toggles[this.selectedStore][idx][key] = !checked;
    queueMicrotask(() => {
      this.toggles[this.selectedStore][idx][key] = checked;
    });
  }

  resetRandSnapConstraints(storeId: string, idx: number): void {
    this.randSnapConstraints[storeId][idx] = this.rsMaxSelectableDigits(this.priceBands[storeId][idx], idx, storeId);
  }

  randSnapDigit(inc: -1 | 1, pb: PriceBand, idx: number, storeId: string): void {
    if (inc > 0) {
      if (pb.randSnap.digits < this.randSnapConstraints[storeId][idx].selectable) {
        pb.randSnap.digits += inc;
      }
    } else {
      if (pb.randSnap.digits > 1) {
        pb.randSnap.digits += inc;
      }
    }
  }

  async addCentSnap(idx: number, storeId: string = this.selectedStore): Promise<void> {
    const pb = this.priceBands[storeId][idx];
    const error = this.invalidSnaps(pb);

    if (error) {
      await this.presentInfoModal(
        'Invalid Snaps Detected',
        [
          'Please make sure all existing cent snaps are valid before creating more.',
          `Cent Snap ${this.centSnapsIDs[error[0]]}: ${error[1]}`,
        ],
        IconsEnum.alertBox,
        IconColourEnum.red,
      );
      return;
    }
    if (!pb.centSnaps) {
      pb.centSnaps = [];
    }

    if (pb.centSnaps.length === this.centSnapsIDs.length) {
      await this.presentInfoModal(
        'Max Snaps Reached',
        [`Currently cent snaps are limited to ${this.centSnapsIDs.length} per price band rule.`],
      );
      return;
    }
    pb.centSnaps.push({range: {start: {v: null, inc: true}, end: {v: null, inc: true}}, v: 0});
    const og = this.ogPriceBands[storeId][idx] ? this.ogPriceBands[storeId][idx] : {};
    if (!og.centSnaps) {
      og.centSnaps = [];
    }
    og.centSnaps.push(null);
    this.ogPriceBands[storeId][idx] = og;
    // pb.centSnaps.push()
  }

  async addRandSnap(idx: number, storeId: string = this.selectedStore): Promise<void> {
    const pb = this.priceBands[storeId][idx];
    const error = this.invalidSnaps(pb, 'rand');

    if (error) {
      await this.presentInfoModal(
        'Invalid Snaps Detected',
        [
          'Please make sure all existing rand snaps are valid before creating more.',
          `Rand Snap **${this.randSnapsIDs[error[0]]}**: ${error[1]}`,
        ],
        IconsEnum.alertBox,
        IconColourEnum.red,
      );
      return;
    }
    if (!pb.randSnap.snaps) {
      pb.randSnap.snaps = [];
    }

    if (pb.randSnap.snaps.length === this.randSnapsIDs.length) {
      await this.presentInfoModal(
        'Max Snaps Reached',
        [`Currently rand snaps are limited to ${this.randSnapsIDs.length} per price band rule.`],
      );
      return;
    }
    pb.randSnap.snaps.push({range: {start: {v: null, inc: true}, end: {v: null, inc: true}}, v: 0});
    const og = this.ogPriceBands[storeId][idx] ? this.ogPriceBands[storeId][idx] : {};
    if (!og.randSnap) {
      og.randSnap = {snaps: [], digits: null};
    }
    og.randSnap.snaps.push(null);
    this.ogPriceBands[storeId][idx] = og;
  }

  async remove(pbIdx: number, snapIdx?: number, snapType: SnapType = 'cents', storeId: string = this.selectedStore): Promise<void> {
    const snapLabel = `${snapType[0].toUpperCase() + snapType.substring(1)} Snap`;
    const confirmed = snapIdx !== null && snapIdx !== undefined
      ? await this.presentConfirmModal(
        `Remove ${snapLabel}`,
        [`Are you sure you want to remove ${snapLabel} ${this.centSnapsIDs[snapIdx]} of rule ${pbIdx + 1}`],
        'Yes',
        'No',
      )
      : await this.presentConfirmModal(
        'Delete Rule',
        [`Are you sure you want rule ${pbIdx + 1}`],
        'Yes',
        'No',
      );

    if (confirmed) {
      this.randSnapConstraints[storeId].splice(pbIdx, 1);
      if (snapIdx !== null && snapIdx !== undefined) {
        if (snapType === 'cents') {
          this.priceBands[storeId][pbIdx].centSnaps.splice(snapIdx, 1);
          this.ogPriceBands[storeId][pbIdx].centSnaps.splice(snapIdx, 1);
        } else {
          this.priceBands[storeId][pbIdx].randSnap.snaps.splice(snapIdx, 1);
          this.ogPriceBands[storeId][pbIdx].randSnap.snaps.splice(snapIdx, 1);
        }
      } else {
        this.priceBands[storeId].splice(pbIdx, 1);
        this.ogPriceBands[storeId].splice(pbIdx, 1);
      }
    }
  }

  async shiftSnap(
    idx: number,
    snapIdx: number,
    snapType: SnapType, shift: -1 | 1,
    storeId: string = this.selectedStore,
  ): Promise<void> {
    const pb = this.priceBands[storeId][idx];
    const og = this.ogPriceBands[storeId]
      ? (idx < this.ogPriceBands[storeId].length ? this.ogPriceBands[storeId][idx] : null)
      : null;

    const snaps = snapType === 'cents' ? pb.centSnaps : pb.randSnap.snaps;
    let temp = snaps[snapIdx];
    snaps[snapIdx] = snaps[snapIdx + shift];
    snaps[snapIdx + shift] = temp;
    const ogSnaps = og ? (snapType === 'cents' ? og.centSnaps : (og.randSnap ? og.randSnap.snaps : null)) : null;

    if (ogSnaps) {
      temp = ogSnaps[snapIdx];
      ogSnaps[snapIdx] = ogSnaps[snapIdx + shift];
      ogSnaps[snapIdx + shift] = temp;
    }
    // const ogs = this.ogPriceBands[]
  }

  setSnapValue(event: Event, pb: PriceBand, i: number, snapType: SnapType = 'cents'): void {
    const inputValue = PriceBandingComponent.getInputValue(event);
    const snap = snapType === 'cents' ? pb.centSnaps[i] : pb.randSnap.snaps[i];
    const v = +inputValue;
    let valid = false;

    if (inputValue !== '') {
      const max = snapType === 'cents' ? 99 : PriceBandingComponent.nines(pb.randSnap.digits);

      if (!isNaN(v) && v >= 0 && v <= max && /^\d+$/.test(inputValue)) {
        valid = true;
      }
    }
    const inputElement = PriceBandingComponent.getInputElement(event);
    const classNames = inputElement.className.split(' ')
      .filter((cn) => cn !== 'invalid').join(' ');

    if (!valid) {
      inputElement.className = classNames + ' invalid';

      if (isNaN(v)) {
        snap.v = null;
      } else {
        snap.v = v;
      }
    } else {
      inputElement.className = classNames;
      snap.v = v;
    }
  }

  async check4save(storeId: string = this.selectedStore): Promise<void> {

    if (!this.priceBands || !this.priceBands[storeId]) {
      return;
    }
    const err = this.checkForOverlaps();

    if (err) {
      await this.presentInfoModal(
        'Price Band Overlaps',
        [
          'Overlapping Price bands can result in contradictions. They are not allowed.',
          err,
        ],
        IconsEnum.alertBox,
        IconColourEnum.red,
      );
      return;
    }
    const errors: {index: number; error: string}[] = [];

    for (let i = 0; i < this.priceBands[storeId].length; i++) {
      const pd = this.priceBands[storeId][i];
      let error = this.invalidSnaps(pd, 'rand');

      if (error) {
        errors.push({index: i, error: `Rand Snap ${this.randSnapsIDs[error[0]]} - ${error[1]}`});
      } else {
        error = this.invalidSnaps(pd, 'cents');

        if (error) {
          errors.push({index: i, error: `Cent Snap ${this.centSnapsIDs[error[0]]} - ${error[1]}`});
        }
      }
    }

    if (errors.length > 0) {
      await this.presentInfoModal(
        'Unresolved Snap Errors',
        [
          `${errors.length} rule(s) have cent snap errors.`,
          ...errors.map((e: {index: number; error: string}) => `Rule ${e.index + 1}: ${e.error}`),
        ],
        IconsEnum.alertBox,
        IconColourEnum.red,
      );
      return;
    } else {
      if (this.priceBands[storeId].length > 0) {
        const changes: Record<number, PriceBand> = {};
        this.priceBands[storeId].forEach((pb, idx) => changes[idx] = pb);
        this.setChanges('price_bands', changes, storeId, storeId !== '' ? 'shared' : 'user');
      } else {
        this.setChanges('price_bands', {}, storeId, storeId !== '' ? 'shared' : 'user');
      }
      const which = storeId === '' ? 'your **personal**' : `${this.stores.stores[storeId].name}'s`;
      await this.presentInfoModal(
        'Changes Staged',
        [
          'Your changes are staged for saving. Hit the save button when you`re happy.',
          `You have staged changes to ${which} price bands.`,
          `If you made changes to other stores${storeId !== '' ? ' or your personal settings' : ''} you need to stage those seporatly (for now).`,
        ],
        IconsEnum.checkCircle,
        IconColourEnum.green,
      );
    }
  }

  async paste(): Promise<void> {
    const storeId = this.selectedStore;

    if (!this.copiedPriceBand || this.copiedPriceBand.length === 0) {

      return;
    }

    if (!this.priceBands[storeId]) {
      this.priceBands[storeId] = [];
    }

    for (let i = 0; i < this.copiedPriceBand.length; i++) {
      // const newPb = await this.linkDepartments(this.copiedPriceBand[i], storeId);
      const newPb = this.copiedPriceBand[i];
      this.priceBands[storeId].push(newPb);
      // this.ogPriceBands[storeId].push(newPb);

      // Set toggles for the copy
      this.toggles[storeId].push({range: !!newPb.range, randSnap: !!newPb.randSnap});
      this.hiddenPBs[storeId].push({departmentFilters: true});
      const maxSelectableDigits = this.rsMaxSelectableDigits(newPb, this.priceBands[storeId].length, storeId);
      // maxSelectableDigits is dependent on toggles and idx for now
      this.randSnapConstraints[storeId].push(maxSelectableDigits);

      // Create a new copy, the previous one was assigned and is mutable.
      this.copiedPriceBand[i] = PriceBandingComponent.copyPB(this.copiedPriceBand[i]);
    }
    this.clear();
  }

  clear(): void {
    this.copiedPriceBand = [];
    this.copiedIdx = [];
  }

  copyRule(pb: PriceBand, idx: number, storeId: string = this.selectedStore): void {
    this.copiedStoreId = storeId;
    this.copiedIdx = [idx];
    this.copiedPriceBand = [PriceBandingComponent.copyPB(pb)];
  }

  copyAll(storeId: string = this.selectedStore): void {
    let idx = 0;
    this.copiedStoreId = storeId;
    this.copiedPriceBand = this.priceBands[storeId].map((pb) => {
      this.copiedIdx.push(idx);
      idx++;
      return PriceBandingComponent.copyPB(pb);
    });
  }

  makeLinks(
    what: LinkedDepartmentsLinkType, fromStoreID: string, fromDepID: string, toStoreID: string, toDepID: string,
  ): void {
    const fIdx = '' + this.depLinks.idxLookUp[fromStoreID];
    const tIdx = '' + this.depLinks.idxLookUp[toStoreID];
    initObj<LinkedDepartments, string[]>([fIdx, what, fromDepID, tIdx], [toDepID], this.depLinks);
    initObj([tIdx, what, toDepID, fIdx], [fromDepID], this.depLinks);
  }

  async linkDepModal(
    whatLink: LinkedDepartmentsLinkType, fromDeparts: DepInfo[], toDeparts: DepInfo[], noSuggestions: string[],
    fromStoreID: string, toStoreID: string,
  ): Promise<string[]> {

    if (fromDeparts.length !== toDeparts.length) {
      throw Error('Suggested links, oldDepartments and toDepartments are not of equal length.');
    }
    const suggestLinks: SuggestedLink[] = fromDeparts.map((a, idx) => ({storeA: a, storeB: toDeparts[idx]}));

    const modal = await this.modalController.create({  // oldDepartments, departments
      component: UnlinkedDepartmentsModalComponent,
      componentProps: {
        type: whatLink, suggestLinks, storeABIDs: {storeA: fromStoreID, storeB: toStoreID}, noLinks: noSuggestions,
      },
      cssClass: 'settings-modal',
    });
    await modal.present();
    const {data} = await modal.onDidDismiss();

    if (data) {
      const resolved: Record<string, DepInfo>[] = data;
      return resolved.map((info) => {

        if (this.makeNewLinks[toStoreID]) {
          this.makeLinks(whatLink, fromStoreID, info[fromStoreID].depId, toStoreID, info[toStoreID].depId);
        }
        return info[toStoreID].depId;
      });
    }
    return null;
  }

  private async initDepLinks(targetStoreID: string): Promise<{fromStoreIdx: number, toStoreIdx: number}> {
    let fromStoreIdx = this.depLinks?.idxLookUp[this.copiedStoreId];
    let toStoreIdx = this.depLinks?.idxLookUp[targetStoreID];

    if (fromStoreIdx !== undefined && toStoreIdx !== undefined) {
      return {fromStoreIdx, toStoreIdx};
    }

    if (fromStoreIdx === undefined) {
      // Initialise store link TODO: Move this to a depLinks function?
      fromStoreIdx = Object.keys(this.depLinks.idxLookUp).length;
      this.depLinks.idxLookUp[this.copiedStoreId] = fromStoreIdx;
      this.depLinks.sIdLookUp[fromStoreIdx] = this.copiedStoreId;
      this.depLinks[fromStoreIdx] = {dep: {}, sub: {}};
    }

    if (toStoreIdx === undefined) {
      // Initialise store link TODO: Move this to a depLinks function?
      toStoreIdx = Object.keys(this.depLinks.idxLookUp).length;
      this.depLinks.idxLookUp[targetStoreID] = toStoreIdx;
      this.depLinks.sIdLookUp[toStoreIdx] = targetStoreID;
      this.depLinks[toStoreIdx] = {dep: {}, sub: {}};
    }

    const createLink = await this.presentConfirmModal(
      'No Links Between Stores',
      [
        'Links are needed between stores when copying over departments and sub-departments',
        'If you do not create links between these stores the next time you try to copy over a priceBand you will have to manually set links again.',
      ],
      'Create Link',
      'Do Not Link',
    );

    if (createLink) {
      this.makeNewLinks[targetStoreID] = true;
    }
    return {fromStoreIdx, toStoreIdx};
  }

  private async linkDepartments(copiedPb: PriceBand, storeId: string): Promise<PriceBand> {
    //const copiedPbCopy = JSON.parse(JSON.stringify(copiedPb));
    const {fromStoreIdx, toStoreIdx} = await this.initDepLinks(storeId);

    if (toStoreIdx === undefined || fromStoreIdx === undefined) {
      return copiedPb;
    }
    const linkedTypeOptions: {what: string; key: LinkedDepartmentsLinkType}[] =
      [{what: 'departs', key: 'dep'}, {what: 'subDeparts', key: 'sub'}];

    for (const {what, key} of linkedTypeOptions) {
      let newIDs2Use = [];

      const unLinked = [];
      const unLinkable = [];
      const copiedDLinks = this.depLinks[fromStoreIdx];

      /*
        key:
        fromDeparts:    list of copiedDepartments where id exists in target
        toDeparts:      list of target departments where id exists in copied
        unLinkableName: list of departments that have no matches or similar thing
      */

      // TODO: Clean this up. Longer then it needs to be
      if (copiedPb[what]) {
        // for (const depart of copiedPb[what]) {
        //   if (copiedDLinks && copiedDLinks[key] && copiedDLinks[key][depart]) {
        //     const linkedDept = this.depLinks[fromStoreIdx][key][depart][toStoreIdx];
        //     if (linkedDept) {
        //       newIDs2Use.push(linkedDept);
        //     } else if( !this.departments[storeId][key][depart]) {
        //       unLinkable.push(depart);
        //     } else {
        //       unLinked.push(depart);
        //     }
        //   } else {
        //     unLinked.push(depart);
        //   }
        // }
        if (unLinked.length > 0 || unLinkable.length > 0) {
          const toDeparts: DepInfo[] = [];
          const fromDeparts: DepInfo[] = [];
          const unLinkableName = [];

          for (const entryID of unLinked) {
            if (this.departments[storeId][key] && this.departments[storeId][key][entryID]) {
              toDeparts.push({name: this.departments[storeId][key][entryID].name, depId: entryID});
              fromDeparts.push({name: this.departments[this.copiedStoreId][key][entryID].name, depId: entryID});
            } else {
              unLinkableName.push(this.departments[this.copiedStoreId][key][entryID].name);
            }
          }
          const resolvedIDs = await this.linkDepModal(key, fromDeparts, toDeparts, unLinkableName, this.copiedStoreId, storeId);

          if (resolvedIDs && resolvedIDs.length) {
            newIDs2Use = newIDs2Use.concat(resolvedIDs);
          }
        }

        if (newIDs2Use.length) {
          copiedPb[what] = newIDs2Use;
        }
      }
    }
    return copiedPb;
  }

  private rsMaxSelectableDigits(pb: PriceBand, idx: number, storeId: string): {digs: number; selectable: number} {
    if (this.toggles[storeId][idx].range && pb.range && pb.range.end && pb.range.end.v) {
      const end = pb.range.end.v;
      const digs = ('' + end).length;

      if (end < 1000) {
        if (end > 10) {
          return {digs, selectable: 1};
        }
        return null;
      } else if (end < 10000) {
        return {digs, selectable: 2};
      } else {
        return {
          digs, selectable: ('' + end).length - 3,
        };
      }
    }
    return {digs: 6, selectable: 3};
    // if (pb.range && pb.range.end && pb.range.end.v) {
    //
    //   return Math.floor(Math.log10(pb.range.end.v)) + 1;
    // }
    // return pb.randSnaps.digit ? pb.randSnaps.digit + 3 : 4;
  }

  private checkForOverlaps(): string {
    // const personal = this.priceBands[''];
    //
    // /*
    // PERSONAL     |    STORE X
    // pb  pb  pb |  pb  pb  pb
    // __________
    //     ______
    // ________________________
    //     ____________________
    //         ________________
    //               __________
    //                   ______
    // */
    //
    // // Check for personal overlaps first as it's joined with all stores
    // for (let i = 0; i < personal.length; i++) {
    //   for (let j = i +1; j < personal.length; j++) {
    //     if (this.priceBandOverLap(personal[i], personal[j])) {
    //       return `Personal Price Bands ${i +1} and ${j +1} overlap.`;
    //     }
    //   }
    // }

    for (const storeId of Object.keys(this.priceBands)) {
      if (this.priceBands[storeId] && this.priceBands[storeId].length) { // && storeId !== ''
        // for (let i = 0; i < personal.length; i++) {
        //   for (let j = 0; j < this.priceBands[storeId].length; j++) {
        //     if (this.priceBandOverLap(personal[i], this.priceBands[storeId][j])) {
        //       return `Personal Price Band ${i +1} overlaps with Price Band ${j +1} of ` +
        //         `${this.stores.stores[storeId].name}.`;
        //     }
        //   }
        // }

        for (let i = 0; i < this.priceBands[storeId].length; i++) {
          for (let j = i + 1; j < this.priceBands[storeId].length; j++) {
            if (this.priceBandOverLap(this.priceBands[storeId][i], this.priceBands[storeId][j])) {
              return `Price Bands ${i + 1} and ${j + 1} of ${this.stores.stores[storeId].name} overlap. Remember you
              can filter departments`;
            }
          }
        }
      }
    }
    return null;
  }

  private priceBandOverLap(pb0: PriceBand, pb1: PriceBand): boolean {
    if (pb0.range && pb1.range) {
      if (!rangeOverlap(pb0.range, pb1.range)) {
        return false;
      }
    }
    let subDepOverlap = false;
    let depOverlap = false;

    if (pb0.subDeparts && pb0.subDeparts.length && pb1.subDeparts && pb1.subDeparts.length) {
      if (pb0.subDeparts.some((sd) => pb1.subDeparts.includes(sd)) ||
        pb1.subDeparts.some((sd) => pb0.subDeparts.includes(sd))) {
        subDepOverlap = true;
      }
    } else {
      subDepOverlap = true;
    }

    if (!subDepOverlap) {
      if (pb0.departs && pb0.departs.length && pb1.departs && pb1.departs.length) {
        if (pb0.departs.some((sd) => pb1.departs.includes(sd)) ||
          pb1.departs.some((sd) => pb0.departs.includes(sd))) {
          depOverlap = true;
        }
      } else {
        depOverlap = true;
      }
    }
    return subDepOverlap || depOverlap;
  }

  private invalidSnaps(pb: PriceBand, snapType: SnapType = 'cents'): (string | number)[] {
    const {snaps, range} = snapType === 'cents' ?
      {snaps: pb.centSnaps, range: pb.range} :
      (pb.randSnap ? {snaps: pb.randSnap.snaps, range: PriceBandingComponent.randSnapRange(pb.randSnap.digits)} :
        {snaps: null, range: null});

    if (!snaps || snaps.length === 0) {
      return;
    }

    for (let i = 0; i < snaps.length; i++) {
      const error = PriceBandingComponent.invalidSnap(snaps[i], snapType, range);
      if (error) {
        return [i, error];
      }
    }

    for (let i = 0; i < snaps.length; i++) {
      for (let j = i + 1; j < snaps.length; j++) {
        if (rangeOverlap(snaps[i].range, snaps[j].range)) {
          return [i, `Range overlap between ${snapType} snap **${this.snapID(i, snapType)}** and ` +
          `**${this.snapID(j, snapType)}**`];
        }
        if (inRange(snaps[i].v, snaps[j].range)) {
          return [i, `The snap value of ${snapType} snap **${this.snapID(i, snapType)}** falls within the range ` +
          `of ${snapType} snap ${this.snapID(j, snapType)}`];
        }
        if (inRange(snaps[j].v, snaps[i].range)) {
          return [j, `The snap value of ${snapType} snap **${this.snapID(j, snapType)}** falls within the range ` +
          `of ${snapType} snap ${this.snapID(i, snapType)}`];
        }
      }
    }
  }

  private snapID(i: number, snapType: SnapType): string {
    return snapType === 'cents' ? this.centSnapsIDs[i] : this.randSnapsIDs[i];
  }

  /**
   * Shows an informational or error modal with a single OK action.
   */
  private async presentInfoModal(
    modalTitle: string,
    contextDescription: string[],
    modalTitleIcon: IconsEnum = IconsEnum.infoSlabCircleOutline,
    modalTitleIconColor: IconColourEnum = IconColourEnum.primary,
  ): Promise<void> {
    await this.sharedModalUtils.createModal({
      modalTitle,
      modalTitleIcon,
      modalTitleIconColor,
      contextDescription,
      buttonAcceptText: 'OK',
      buttonCloseText: '',
      buttonAccept: true,
      buttonClose: false,
      buttonCloseCross: false,
      cssClass: ['shared-basic-modal-css-smaller'],
    });
  }

  /**
   * Shows a confirmation modal and returns whether the accept button was pressed.
   */
  private async presentConfirmModal(
    modalTitle: string,
    contextDescription: string[],
    buttonAcceptText: string,
    buttonCloseText: string,
  ): Promise<boolean> {
    const result = await this.sharedModalUtils.createModal({
      modalTitle,
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextDescription,
      buttonAcceptText,
      buttonCloseText,
      buttonAccept: true,
      buttonClose: true,
      buttonCloseCross: false,
      cssClass: ['shared-basic-modal-css-smaller'],
    });
    return result.data?.buttonPressed === 'button-accept';
  }

  trackByStoreId(_: number, storeId: string): string {
    return storeId;
  }

  trackByPriceBand(index: number): number {
    return index;
  }

  trackBySnap(index: number): number {
    return index;
  }

}
